import React, { useState } from "react";
import "./Portfolio.css";
import Sidebar from "./Sidebar/Sidebar";
import Overview from "./Overview/Overview";
import Orders from "./Orders/Orders";
import History from "./History/History";
import Position from "./Position/Position";
import { useWeb3React } from "@web3-react/core";
import { useChainId } from "lib/chains";
import { getPositions } from "pages/Exchange/getPositions";
import { getPositionQuery } from "pages/Exchange/getPositionQuery";
import { getContract } from "config/contracts";
import { getTokens, getWhitelistedTokens } from "config/tokens";
import { contractFetcher } from "lib/contracts";
import useSWR from "swr";
import Reader from "abis/Reader.json";
import { BigNumber } from "ethers";
import { useAccountOrders } from "lib/legacy";
import Vault from "abis/Vault.json";
import Token from "abis/Token.json";
import { useInfoTokens } from "domain/tokens";

const componentMap = {
  overview: Overview,
  position: Position,
  orders: Orders,
  history: History,
};

const Portfolio = ({
  savedIsPnlInLeverage,
  savedShowPnlAfterFees,
  connectWallet,
  savedShouldDisableValidationForTesting,
  pendingTxns,
  setPendingTxns,
  isCancelMultipleOrderProcessing,
  cancelOrderIdList,
  setCancelOrderIdList,
  onMultipleCancelClick,
  setMarket,
}) => {
  const [pendingPositions, setPendingPositions] = useState({});
  const [updatedPositions] = useState({});

  const [activePage, setActivePage] = useState("overview");
  const [activeFilter, setActiveFilter] = useState("");
  const PortfolioActiveComponent = componentMap[activePage];

  const { chainId } = useChainId();

  const { active, account, library } = useWeb3React();

  const whitelistedTokens = getWhitelistedTokens(chainId);

  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");
  const readerAddress = getContract(chainId, "Reader");
  const vaultAddress = getContract(chainId, "Vault");
  const tokens = getTokens(chainId);
  const usdgAddress = getContract(chainId, "USDG");

  const positionQuery = getPositionQuery(whitelistedTokens, nativeTokenAddress);

  const { data: positionData, error: positionDataError } = useSWR(
    active && [active, chainId, readerAddress, "getPositions", vaultAddress, account],
    contractFetcher(library, Reader, [positionQuery.indexTokens, positionQuery.isLong])
  );
  const tokenAddresses = tokens.map((token) => token.address);

  const { data: tokenBalances } = useSWR(
    active && [active, chainId, readerAddress, "getTokenBalances", account],
    contractFetcher(library, Reader, [tokenAddresses])
  );

  const whitelistedTokenAddresses = whitelistedTokens.map((token) => token.address);

  const { data: fundingRateInfo } = useSWR(
    [active, chainId, readerAddress, "getFundingRates"],
    contractFetcher(library, Reader, [vaultAddress, nativeTokenAddress, whitelistedTokenAddresses])
  );

  const { data: totalTokenWeights } = useSWR(
    [`Exchange:totalTokenWeights:${active}`, chainId, vaultAddress, "totalTokenWeights"],
    contractFetcher(library, Vault)
  );

  const { data: usdgSupply } = useSWR(
    [`Exchange:usdgSupply:${active}`, chainId, usdgAddress, "totalSupply"],
    contractFetcher(library, Token)
  );

  const { infoTokens } = useInfoTokens(
    library,
    chainId,
    active,
    tokenBalances as BigNumber[],
    fundingRateInfo as BigNumber[]
  );

  const { positions, positionsMap } = getPositions(
    chainId,
    positionQuery,
    positionData,
    infoTokens,
    savedIsPnlInLeverage,
    savedShowPnlAfterFees,
    account,
    pendingPositions,
    updatedPositions
  );

  const flagOrdersEnabled = true;
  const [orders] = useAccountOrders(flagOrdersEnabled, false);

  return (
    <div className="Portfolio">
      <Sidebar setActivePage={setActivePage} activePage={activePage} />
      <PortfolioActiveComponent
        savedIsPnlInLeverage={savedIsPnlInLeverage}
        savedShowPnlAfterFees={savedShowPnlAfterFees}
        connectWallet={connectWallet}
        active={active}
        account={account}
        library={library}
        chainId={chainId}
        positions={positions}
        positionsMap={positionsMap}
        nativeTokenAddress={nativeTokenAddress}
        infoTokens={infoTokens}
        whitelistedTokens={whitelistedTokens}
        positionData={positionData}
        positionDataError={positionDataError}
        pendingPositions={pendingPositions}
        setPendingPositions={setPendingPositions}
        orders={orders}
        totalTokenWeights={totalTokenWeights}
        usdgSupply={usdgSupply}
        savedShouldDisableValidationForTesting={savedShouldDisableValidationForTesting}
        pendingTxns={pendingTxns}
        setPendingTxns={setPendingTxns}
        isCancelMultipleOrderProcessing={isCancelMultipleOrderProcessing}
        cancelOrderIdList={cancelOrderIdList}
        setCancelOrderIdList={setCancelOrderIdList}
        onMultipleCancelClick={onMultipleCancelClick}
        setMarket={setMarket}
        setActivePage={setActivePage}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
      />
    </div>
  );
};

export default Portfolio;
